/**
 * The sign-in redirect of the three OAuth connectors (Google Drive, Office 365,
 * Atlassian), for its two targets (MYAC-1515):
 *
 * - a **datasource** of a QMS or a product — the path that has always existed.
 *   Its `state`, what it leaves in sessionStorage and its authorize URL are
 *   pinned by `stores/__tests__/datasources-oauth-redirect.test.ts`: the callback
 *   pages and the sync services read them as they are.
 * - a **connection** of the company. Its state names the company and the
 *   connection (`tid`, `cid`) where the datasource one names `pid` and `dsid`,
 *   and everything the callback needs sits in ONE session entry whose key no
 *   datasource entry can take.
 *
 * Nothing here touches Firestore: the stores decide what exists before the
 * redirect and what is written after it.
 */
import { DatasourceType } from '@/models/Datasource'
import { atlassianRedirectUri } from '@/utils/atlassianOAuth'
import { generateUrlSafeRandomString, newCsrfToken, sha256Base64Url } from '@/utils/crypto'
import { logger } from '@/utils/logger'

export const GOOGLE_DRIVE_SCOPES = ['https://www.googleapis.com/auth/drive.readonly', 'openid', 'email']

export const OFFICE_365_SCOPES = [
  'https://graph.microsoft.com/Sites.Read.All',
  'https://graph.microsoft.com/Files.Read.All',
  'offline_access',
  'openid',
  'email',
  'profile',
]

// Atlassian 3LO scopes. Jira: classic (work for v3 REST). Confluence: granular
// (required by the v2 API). offline_access → refresh token. Must match the app.
export const ATLASSIAN_SCOPES = [
  'read:jira-work',
  'read:jira-user',
  'read:space:confluence',
  'read:page:confluence',
  'read:blogpost:confluence',
  'read:database:confluence',
  'read:attachment:confluence',
  'search:confluence',
  'offline_access',
]

/** The connectors that sign in through a redirect; the others take credentials in a form. */
export type OAuthConnectorType = DatasourceType.GOOGLE_DRIVE | DatasourceType.OFFICE_365 | DatasourceType.ATLASSIAN

export function isOAuthConnector(type: DatasourceType | string | undefined): type is OAuthConnectorType {
  return type === DatasourceType.GOOGLE_DRIVE || type === DatasourceType.OFFICE_365 || type === DatasourceType.ATLASSIAN
}

/** What the sign-in is for. */
export type OAuthTarget =
  | { kind: 'datasource'; pid: string; dsid: string }
  /** `cloudId`: an Atlassian RECONNECT only — the site of the connection being signed in again. */
  | { kind: 'connection'; tid: string; cid: string; name?: string; cloudId?: string }

/** The OAuth client of a connector in this environment, or undefined when it is not configured. */
export function oauthClientId(type: OAuthConnectorType): string | undefined {
  const env = import.meta.env
  const id =
    type === DatasourceType.GOOGLE_DRIVE
      ? env.VITE_GOOGLE_CLIENT_ID
      : type === DatasourceType.OFFICE_365
        ? env.VITE_MS_CLIENT_ID
        : env.VITE_ATLASSIAN_CLIENT_ID
  return (id as string | undefined) || undefined
}

export function googleDriveRedirectUri(): string {
  return `${window.location.origin}/callbacks/google_drive_oauth`
}

export function office365RedirectUri(): string {
  return `${window.location.origin}/callbacks/office365_delegated_oauth`
}

/** Key order is part of the datasource contract: the callback compares nothing but values, the pin test compares bytes. */
function targetState(target: OAuthTarget, csrf: string): Record<string, unknown> {
  if (target.kind === 'datasource') return { pid: target.pid, dsid: target.dsid, csrf }
  return target.name ? { tid: target.tid, cid: target.cid, csrf, name: target.name } : { tid: target.tid, cid: target.cid, csrf }
}

function stash(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value)
  } catch (e) {
    // Without the stash the callback refuses the sign-in: said here, where it starts.
    logger.error('Could not keep the sign-in state in the session', e)
  }
}

// ── the session entry of a connection sign-in ───────────────────────────────

/** Every datasource entry starts with `gdrive_oauth_`, `office365_delegated_state_` or `atlassian_oauth_state_`. */
const CONNECTION_STASH_PREFIX = 'datasource_connection_oauth_'

/** What a connection sign-in leaves in the session, and what its callback takes back out. */
export interface ConnectionOAuthState {
  tid: string
  cid: string
  csrf: string
  type: OAuthConnectorType
  /** Present for a NEW connection only: a reconnect keeps the name the connection has. */
  name?: string
  /** PKCE verifier (Google Drive, Office 365). */
  verifier?: string
  /** The scopes asked for (Office 365 sends them back with the code). */
  scopes?: string[]
  /**
   * An Atlassian reconnect only: the site the connection is signed in to. It
   * stays in the session, out of the URL, and goes back to the backend with the
   * code — Atlassian lists an account's sites in no fixed order.
   */
  cloud_id?: string
}

function stashConnection(state: ConnectionOAuthState): void {
  stash(`${CONNECTION_STASH_PREFIX}${state.cid}`, JSON.stringify(state))
}

/** What a callback page was handed, when it is about a company connection. */
export type ConnectionOAuthCallback =
  /** Not a connection sign-in: the datasource path of the page handles it. */
  | { kind: 'none' }
  /** A connection state this session did not issue (or already used): nothing may be sent. */
  | { kind: 'invalid' }
  /** This session's sign-in came back without a code: the user declined, or the provider refused. */
  | { kind: 'declined'; state: ConnectionOAuthState }
  | { kind: 'granted'; state: ConnectionOAuthState; code: string }

/**
 * Reads the provider's answer on a callback page. A state that names a
 * connection (`cid`) and no datasource (`dsid`) is a connection sign-in; it is
 * believed only if this session holds the entry it was issued with — same
 * company, same connection, same CSRF token, same connector — and that entry is
 * taken out of the session as it is read, so one sign-in sends one command.
 * The values used afterwards are the session's, never the URL's.
 */
export function readConnectionOAuthCallback(searchParams: URLSearchParams, type: OAuthConnectorType): ConnectionOAuthCallback {
  let returned: Record<string, unknown> | null = null
  try {
    returned = JSON.parse(decodeURIComponent(searchParams.get('state') || ''))
  } catch {
    return { kind: 'none' }
  }
  if (!returned || typeof returned !== 'object') return { kind: 'none' }
  if (typeof returned.cid !== 'string' || !returned.cid || returned.dsid) return { kind: 'none' }

  const key = `${CONNECTION_STASH_PREFIX}${returned.cid}`
  let stashed: ConnectionOAuthState | null = null
  try {
    stashed = JSON.parse(sessionStorage.getItem(key) || 'null')
    sessionStorage.removeItem(key)
  } catch {
    stashed = null
  }
  if (
    !stashed ||
    !stashed.csrf ||
    stashed.csrf !== returned.csrf ||
    stashed.cid !== returned.cid ||
    !stashed.tid ||
    stashed.tid !== returned.tid ||
    stashed.type !== type
  ) {
    return { kind: 'invalid' }
  }

  const code = searchParams.get('code') || ''
  return code ? { kind: 'granted', state: stashed, code } : { kind: 'declined', state: stashed }
}

// ── the three redirects ─────────────────────────────────────────────────────

/** Authorization Code + PKCE. The verifier stays in the session, never in the URL. */
export async function redirectToGoogleAuthorize(target: OAuthTarget, clientId: string): Promise<void> {
  const csrf = newCsrfToken()
  const verifier = generateUrlSafeRandomString(64)
  const state = targetState(target, csrf)
  if (target.kind === 'datasource') {
    stash('gdrive_oauth_state', csrf)
    stash(`gdrive_oauth_verifier_${target.dsid}`, verifier)
  } else {
    stashConnection({ tid: target.tid, cid: target.cid, csrf, type: DatasourceType.GOOGLE_DRIVE, name: target.name, verifier })
  }
  const challenge = await sha256Base64Url(verifier)
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: googleDriveRedirectUri(),
    response_type: 'code',
    scope: GOOGLE_DRIVE_SCOPES.join(' '),
    include_granted_scopes: 'true',
    state: encodeURIComponent(JSON.stringify(state)),
    prompt: 'consent',
    access_type: 'offline',
    code_challenge: challenge,
    code_challenge_method: 'S256',
  })
  window.location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`)
}

/**
 * Delegated flow. A datasource's own callback reads the scopes and the verifier
 * off the state it gets back, so for a datasource they travel in it, as they
 * always have. For a company connection they stay in the session, where its
 * callback reads them: a verifier in the URL sits beside the code in every log
 * and history that keeps the redirect, which is what PKCE exists to prevent.
 */
export async function redirectToOffice365Authorize(target: OAuthTarget, clientId: string): Promise<void> {
  const scopes = OFFICE_365_SCOPES
  const verifier = generateUrlSafeRandomString(64)
  const csrf = newCsrfToken()
  const state = target.kind === 'datasource' ? { ...targetState(target, csrf), scopes, verifier } : targetState(target, csrf)
  if (target.kind === 'datasource') {
    stash(`office365_delegated_state_${target.dsid}`, JSON.stringify(state))
  } else {
    stashConnection({ tid: target.tid, cid: target.cid, csrf, type: DatasourceType.OFFICE_365, name: target.name, verifier, scopes })
  }
  const challenge = await sha256Base64Url(verifier)
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: office365RedirectUri(),
    response_type: 'code',
    scope: scopes.join(' '),
    state: encodeURIComponent(JSON.stringify(state)),
    prompt: 'consent',
    code_challenge: challenge,
    code_challenge_method: 'S256',
  })
  window.location.assign(`https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`)
}

/** 3LO, no PKCE: a confidential client, the backend exchanges the code with its secret. */
export function redirectToAtlassianAuthorize(target: OAuthTarget, clientId: string): void {
  const csrf = newCsrfToken()
  const state = targetState(target, csrf)
  if (target.kind === 'datasource') {
    stash(`atlassian_oauth_state_${target.dsid}`, csrf)
  } else {
    stashConnection({
      tid: target.tid,
      cid: target.cid,
      csrf,
      type: DatasourceType.ATLASSIAN,
      name: target.name,
      // Left out rather than stored empty: a new connection has no site yet.
      ...(target.cloudId ? { cloud_id: target.cloudId } : {}),
    })
  }
  const params = new URLSearchParams({
    audience: 'api.atlassian.com',
    client_id: clientId,
    scope: ATLASSIAN_SCOPES.join(' '),
    redirect_uri: atlassianRedirectUri(),
    state: encodeURIComponent(JSON.stringify(state)),
    response_type: 'code',
    prompt: 'consent',
  })
  window.location.assign(`https://auth.atlassian.com/authorize?${params.toString()}`)
}

/** The redirect of a connector, whichever it is. Resolves once the browser was told to leave. */
export async function redirectToAuthorize(type: OAuthConnectorType, target: OAuthTarget, clientId: string): Promise<void> {
  if (type === DatasourceType.GOOGLE_DRIVE) return redirectToGoogleAuthorize(target, clientId)
  if (type === DatasourceType.OFFICE_365) return redirectToOffice365Authorize(target, clientId)
  redirectToAtlassianAuthorize(target, clientId)
}
